// apps/web/src/lib/outreach-api.ts
import { apiClient } from './api'

export interface OutreachStep { subject: string; bodyHtml: string; delayDays: number }
export interface OutreachCampaign {
  _id: string
  name: string
  status: 'draft' | 'sending' | 'active' | 'paused' | 'done'
  targetTag: string
  fromName: string
  fromEmail: string
  steps: OutreachStep[]
  sendThrottlePerMin: number
  stats: { sent: number; opened: number; clicked: number; replied: number; bounced: number; unsubscribed: number; sendFailed: number; complained: number }
  createdAt: string
  updatedAt: string
}
export interface OutreachThreadRow {
  enrollmentId: string; campaignId: string; contactId: string; email: string; status: string
  lastSentAt?: string | null
  dealId?: string | null
  isDirect?: boolean
  contact: { name: string }
  lastMessage: { direction: string; snippet: string; createdAt: string } | null
}
export interface SendDirectResult {
  status: 'sent' | 'queued' | 'test'
  enrollmentId: string
  messageId?: string
  sentAt?: string
  estimatedSendAt?: string
}
export interface OutreachSendStatus {
  intervalMinutes: number
  lastSentAt: string | null
  nextAvailableAt: string
  windowOpen: boolean
  dailyCapReached: boolean
  testMode: boolean
}
export interface ConvertToDealPayload {
  name: string
  value?: number
  currency?: string
  pipelineId?: string
  stageId?: string
  logActivity?: boolean
}
export interface OutreachDeal { _id: string; name: string; [k: string]: unknown }
export interface OutreachTag { tag: string; count: number }
export interface OutreachMessage {
  _id: string; direction: 'outbound' | 'inbound'; kind?: string
  from?: string; to?: string; subject?: string; bodyHtml?: string; bodyText?: string; snippet?: string
  unmatched?: boolean; openedAt?: string; createdAt: string
  // A draft is an unsent OUTBOUND message — editable/deletable in the contact Emails tab.
  draft?: boolean
}

/**
 * Shared Tailwind classes for an enrollment status pill. Used by both the campaign
 * Recipients panel and the inbox so the colour language stays consistent everywhere.
 */
export function enrollmentStatusBadge(status: string): string {
  switch (status) {
    case 'pending': return 'bg-wash text-ink-3'
    case 'sending': return 'bg-info-tint text-info-text'
    case 'sent': return 'bg-info-tint text-info-text'
    case 'opened': return 'bg-accent-tint text-accent-hover'
    case 'replied': return 'bg-success-tint text-success-text'
    case 'bounced': return 'bg-danger-tint text-danger-text'
    case 'unsubscribed': return 'bg-warning-tint text-warning-text'
    case 'completed': return 'bg-wash text-ink-2'
    case 'suppressed': return 'bg-warning-tint text-warning-text'
    case 'send_failed': return 'bg-danger-tint text-danger-text'
    default: return 'bg-wash text-ink-3'
  }
}

/** Human-friendly label for an enrollment status (snake_case → Title Case). */
export function enrollmentStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export const outreachApi = {
  // Campaigns
  listCampaigns: (params?: Record<string, unknown>) =>
    apiClient.getList<OutreachCampaign>('/outreach/campaigns', params),
  getCampaign: (id: string) =>
    apiClient.get<OutreachCampaign>(`/outreach/campaigns/${id}`),
  createCampaign: (data: Partial<OutreachCampaign>) =>
    apiClient.post<OutreachCampaign>('/outreach/campaigns', data),
  updateCampaign: (id: string, data: Partial<OutreachCampaign>) =>
    apiClient.patch<OutreachCampaign>(`/outreach/campaigns/${id}`, data),
  previewRecipients: (id: string) =>
    apiClient.get<{ count: number }>(`/outreach/campaigns/${id}/recipients/preview`),
  listTags: (q?: string) =>
    apiClient.get<OutreachTag[]>('/outreach/campaigns/tags', { q }),
  campaignRecipients: (id: string, params?: Record<string, unknown>) =>
    apiClient.getList<OutreachThreadRow>(`/outreach/campaigns/${id}/recipients`, params),
  testSend: (id: string) =>
    apiClient.post<{ messageId: string }>(`/outreach/campaigns/${id}/test-send`, {}),
  sendCampaign: (id: string) =>
    apiClient.post<{ enrolled: number; queued: number }>(`/outreach/campaigns/${id}/send`, {}),
  pauseCampaign: (id: string) =>
    apiClient.post(`/outreach/campaigns/${id}/pause`, {}),
  resumeCampaign: (id: string) =>
    apiClient.post(`/outreach/campaigns/${id}/resume`, {}),

  // Inbox
  listThreads: (params?: Record<string, unknown>) =>
    apiClient.getList<OutreachThreadRow>('/outreach/inbox', params),
  listUnmatched: (params?: Record<string, unknown>) =>
    apiClient.getList<OutreachMessage>('/outreach/inbox/unmatched', params),
  getThread: (enrollmentId: string) =>
    apiClient.get<{ enrollment: any; contact: any; messages: OutreachMessage[]; dealId?: string | null }>(`/outreach/inbox/${enrollmentId}`),
  contactThreads: (contactId: string) =>
    apiClient.get<OutreachMessage[]>(`/outreach/inbox/contacts/${contactId}`),
  reply: (enrollmentId: string, bodyHtml: string) =>
    apiClient.post<{ messageId: string }>(`/outreach/inbox/${enrollmentId}/reply`, { bodyHtml }),
  convertToDeal: (enrollmentId: string, payload: ConvertToDealPayload) =>
    apiClient.post<OutreachDeal>(`/outreach/inbox/${enrollmentId}/deal`, payload),

  // Direct (custom) email — usable by reps (Member/KAM+). `override` skips pacing and sends now.
  // `draftId` (when sending an edited draft) is consumed server-side after the send.
  sendDirect: (contactId: string, payload: { subject: string; bodyHtml: string; override?: boolean; draftId?: string }) =>
    apiClient.post<SendDirectResult>(`/outreach/direct/${contactId}`, payload),
  sendStatus: () =>
    apiClient.get<OutreachSendStatus>('/outreach/send-status'),

  // Drafts — create/update (omit draftId to create) and delete. Drafts live in the contact Emails tab.
  saveDraft: (contactId: string, payload: { subject: string; bodyHtml: string; draftId?: string }) =>
    apiClient.post<OutreachMessage>(`/outreach/drafts/${contactId}`, payload),
  deleteDraft: (draftId: string) =>
    apiClient.delete(`/outreach/drafts/${draftId}`),

  // Settings (per-org Mailgun config). The API key is write-only and never returned.
  getSettings: () =>
    apiClient.get<OutreachSettings>('/outreach/settings'),
  updateSettings: (data: UpdateOutreachSettingsPayload) =>
    apiClient.patch('/outreach/settings', data),
}

export interface OutreachSendWindow {
  enabled: boolean
  days: number[]      // 0=Sun … 6=Sat
  startHour: number   // 0–23
  endHour: number     // 1–24 (exclusive)
}
export interface OutreachWarmup {
  enabled: boolean
  startedAt: string | null
  initialDaily: number
  dailyIncrement: number
}
export interface OutreachSettings {
  enabled: boolean
  fromName: string
  fromEmail: string
  mailgunDomain: string
  cooldownDays: number
  apiKeyConfigured: boolean
  sendWindow: OutreachSendWindow
  dailySendCap: number
  warmup: OutreachWarmup
  sendIntervalMinutes: number
  testMode: boolean
  lastSentAt: string | null
}
export interface UpdateOutreachSettingsPayload {
  enabled?: boolean
  fromName?: string
  fromEmail?: string
  mailgunDomain?: string
  mailgunApiKey?: string
  cooldownDays?: number
  sendWindow?: Partial<Omit<OutreachSendWindow, never>>
  dailySendCap?: number
  // startedAt is server-managed and never sent from the client.
  warmup?: { enabled?: boolean; initialDaily?: number; dailyIncrement?: number }
  sendIntervalMinutes?: number
  testMode?: boolean
  // lastSentAt is server-managed (slot claim) and never sent from the client.
}
